import { ArtBox, ArtButton, ArtCard, ArtStack, ArtTypography, FaIcon } from "@art/shared-ui";
import {
  faArrowRight,
  faBuildingShield,
  faGlobe,
  faLayerGroup,
  faLock,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import type { RoleDetailContext } from "./index";
import styles from "./details.module.scss";

type AccessCardProps = {
  eyebrow: string;
  title: string;
  description: string;
  icon: typeof faShieldHalved;
  actionLabel: string;
  onAction: () => void;
  tone: "primary" | "secondary";
};

const AccessCard = ({ eyebrow, title, description, icon, actionLabel, onAction, tone }: AccessCardProps) => (
  <ArtCard variant="outlined" className={`${styles.access_card} ${styles[tone]}`}>
    <ArtStack className={styles.access_card_content}>
      <ArtBox className={styles.access_icon} aria-hidden="true"><FaIcon icon={icon} /></ArtBox>
      <ArtBox className={styles.access_copy}>
        <ArtTypography className={styles.eyebrow}>{eyebrow}</ArtTypography>
        <ArtTypography component="h3" className={styles.access_title}>{title}</ArtTypography>
        <ArtTypography className={styles.access_description}>{description}</ArtTypography>
      </ArtBox>
      <ArtButton
        buttonType="button"
        variant={tone === "primary" ? "contained" : "outlined"}
        label={actionLabel}
        icon={faArrowRight}
        onClick={onAction}
      />
    </ArtStack>
  </ArtCard>
);

const RoleDetailInfo = () => {
  const { role, canManageRole, canManageAccess, canManageResourceScopes } =
    useOutletContext<RoleDetailContext>();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!role) return null;

  const roleScope = role.is_master
    ? "Platform role"
    : role.is_tenant_scoped || role.tenant_id != null
      ? "Workspace role"
      : "Shared role";
  const ownershipDescription = canManageRole
    ? "This workspace owns the role and can update its access policy."
    : "This is a protected role. Its policy can be reviewed but not changed here.";

  return (
    <ArtStack className={styles.page}>
      <ArtCard variant="outlined" className={styles.hero}>
        <ArtBox className={styles.hero_glow} aria-hidden="true" />
        <ArtStack className={styles.hero_content}>
          <ArtBox className={styles.role_mark} aria-hidden="true"><FaIcon icon={faBuildingShield} /></ArtBox>
          <ArtBox className={styles.role_identity}>
            <ArtTypography className={styles.eyebrow}>Role overview</ArtTypography>
            <ArtTypography component="h2" className={styles.role_name}>{role.name}</ArtTypography>
            <ArtTypography className={styles.role_description}>
              {role.description || "No description has been added for this role."}
            </ArtTypography>
            <ArtBox className={styles.badges} aria-label="Role classification">
              <span className={styles.badge}><FaIcon icon={faGlobe} />{roleScope}</span>
              <span className={`${styles.badge} ${canManageRole ? styles.editable : styles.protected}`}>
                <FaIcon icon={canManageRole ? faShieldHalved : faLock} />
                {canManageRole ? "Policy editable" : "Protected policy"}
              </span>
            </ArtBox>
          </ArtBox>
        </ArtStack>

        <ArtBox className={styles.fact_grid} component="dl">
          <ArtBox className={styles.fact} component="div">
            <ArtTypography component="dt">Policy scope</ArtTypography>
            <ArtTypography component="dd">{roleScope}</ArtTypography>
          </ArtBox>
          <ArtBox className={styles.fact} component="div">
            <ArtTypography component="dt">Enforcement</ArtTypography>
            <ArtTypography component="dd">Applied immediately</ArtTypography>
          </ArtBox>
          <ArtBox className={styles.fact} component="div">
            <ArtTypography component="dt">Management</ArtTypography>
            <ArtTypography component="dd">{canManageRole ? "Workspace controlled" : "Read only"}</ArtTypography>
          </ArtBox>
        </ArtBox>
      </ArtCard>

      <ArtBox className={styles.section_heading}>
        <ArtBox>
          <ArtTypography component="h2">Access configuration</ArtTypography>
          <ArtTypography>Review what the role can do and where those permissions apply.</ArtTypography>
        </ArtBox>
        <ArtTypography className={styles.policy_note}>
          <FaIcon icon={faShieldHalved} />{ownershipDescription}
        </ArtTypography>
      </ArtBox>

      <ArtBox className={styles.access_grid}>
        {canManageAccess && (
          <AccessCard
            eyebrow="Capabilities"
            title="ABAC access"
            description="Control application features and fine-grained actions available to this role."
            icon={faShieldHalved}
            actionLabel={canManageRole ? "Manage ABAC" : "Review ABAC"}
            onAction={() => navigate(`/platform-settings/roles-permissions/${id}/abac`)}
            tone="primary"
          />
        )}
        {canManageResourceScopes && (
          <AccessCard
            eyebrow="Boundaries"
            title="Resource access"
            description="Define the projects and environments where this role's permissions take effect."
            icon={faLayerGroup}
            actionLabel={canManageRole ? "Manage resources" : "Review resources"}
            onAction={() => navigate(`/platform-settings/roles-permissions/${id}/resource-access`)}
            tone="secondary"
          />
        )}
      </ArtBox>

      {!canManageAccess && !canManageResourceScopes && (
        <ArtCard variant="outlined" className={styles.no_access}>
          <ArtBox className={styles.access_icon} aria-hidden="true"><FaIcon icon={faLock} /></ArtBox>
          <ArtBox>
            <ArtTypography component="h3">Access policy is restricted</ArtTypography>
            <ArtTypography>
              Your current permissions allow you to view this role's identity, but not its policy configuration.
            </ArtTypography>
          </ArtBox>
        </ArtCard>
      )}
    </ArtStack>
  );
};

export default RoleDetailInfo;
